/**
 * Readers for the consent cookies of well-known consent management platforms (CMPs).
 *
 * The booking engine runs on a subdomain (e.g. book.customer.fi) and the CMP banner on the main
 * domain, so the CMP's JavaScript API is not available. Only cookies visible to the subdomain
 * (set for the whole domain) can be read, and only a positive match grants consent.
 * A missing or unrecognised cookie returns null, which makes the caller fall back to its own banner.
 *
 * Cookie formats marked "unverified" are written from memory and must be checked against a real site.
 */

export interface ICmpConsent {
  analytics: boolean;
  marketing: boolean;
}

export type CmpReader = (cookies: Record<string, string>) => ICmpConsent | null;

export const OWN_CONSENT_COOKIE = 'hlx_consent';

const CMP_YES = 'yes';
const CMP_ALLOW = 'allow';
const COOKIEBOT_NO_CONSENT = '0';
const COOKIEBOT_CONSENT_NOT_REQUIRED = '-1';
const ONETRUST_ANALYTICS_GROUP = 'C0002';
const ONETRUST_MARKETING_GROUP = 'C0004';

export function parseCookies(cookieString: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  (cookieString || '').split(';').forEach(part => {
    const separator = part.indexOf('=');
    if (separator < 1) {
      return;
    }
    const name = part.substring(0, separator).trim();
    const value = part.substring(separator + 1).trim();
    try {
      cookies[name] = decodeURIComponent(value);
    } catch {
      cookies[name] = value;
    }
  });
  return cookies;
}

function parseJson(raw: string | undefined): any | null {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Parses "key:value,key:value" lists, used by CookieYes and the own consent cookie */
function parseKeyValueList(raw: string | undefined): Record<string, string> | null {
  if (!raw) {
    return null;
  }
  const result: Record<string, string> = {};
  raw.split(',').forEach(pair => {
    const separator = pair.indexOf(':');
    if (separator > 0) {
      result[pair.substring(0, separator).trim()] = pair.substring(separator + 1).trim();
    }
  });
  return Object.keys(result).length ? result : null;
}

/** Cookiebot: cookie "CookieConsent" = 0 | -1 | {stamp:'..',necessary:true,preferences:..,statistics:..,marketing:..} */
export const readCookiebot: CmpReader = cookies => {
  const raw = cookies['CookieConsent']?.trim();
  if (raw === undefined) {
    return null;
  }
  if (raw === COOKIEBOT_NO_CONSENT) {
    return { analytics: false, marketing: false };
  }
  if (raw === COOKIEBOT_CONSENT_NOT_REQUIRED) {
    return { analytics: true, marketing: true };
  }

  const flag = (key: string) => new RegExp(`['"]?${key}['"]?\\s*:\\s*(true|false)`).exec(raw)?.[1];
  const statistics = flag('statistics');
  const marketing = flag('marketing');
  if (statistics === undefined && marketing === undefined) {
    return null;
  }
  return { analytics: statistics === 'true', marketing: marketing === 'true' };
};

/** CookieYes: cookie "cookieyes-consent" = consentid:..,consent:yes,analytics:yes,advertisement:no,... */
export const readCookieYes: CmpReader = cookies => {
  const values = parseKeyValueList(cookies['cookieyes-consent']);
  if (!values || (values['analytics'] === undefined && values['advertisement'] === undefined)) {
    return null;
  }
  return { analytics: values['analytics'] === CMP_YES, marketing: values['advertisement'] === CMP_YES };
};

/**
 * OneTrust: cookie "OptanonConsent" is a query string with groups=C0001:1,C0002:0,C0004:0.
 * Group ids are configurable, the defaults are used here (C0002 performance, C0004 targeting).
 * OptanonConsent exists before the visitor has chosen, so OptanonAlertBoxClosed is required too.
 */
export const readOneTrust: CmpReader = cookies => {
  const raw = cookies['OptanonConsent'];
  if (!raw || cookies['OptanonAlertBoxClosed'] === undefined) {
    return null;
  }
  const groups = new URLSearchParams(raw).get('groups');
  if (!groups) {
    return null;
  }
  const values = parseKeyValueList(groups) ?? {};
  return { analytics: values[ONETRUST_ANALYTICS_GROUP] === '1', marketing: values[ONETRUST_MARKETING_GROUP] === '1' };
};

/** Complianz (WordPress): cookies cmplz_statistics / cmplz_marketing = allow | deny (unverified) */
export const readComplianz: CmpReader = cookies => {
  const statistics = cookies['cmplz_statistics'];
  const marketing = cookies['cmplz_marketing'];
  if (statistics === undefined && marketing === undefined) {
    return null;
  }
  return { analytics: statistics === CMP_ALLOW, marketing: marketing === CMP_ALLOW };
};

/** Cookie-Script: cookie "CookieScriptConsent" = JSON {action:'accept'|'reject', categories:'["performance","targeting"]'} (unverified) */
export const readCookieScript: CmpReader = cookies => {
  const consent = parseJson(cookies['CookieScriptConsent']);
  if (!consent || typeof consent !== 'object') {
    return null;
  }
  if (consent.action === 'reject') {
    return { analytics: false, marketing: false };
  }

  const categories = typeof consent.categories === 'string' ? parseJson(consent.categories) : consent.categories;
  if (!Array.isArray(categories)) {
    return null;
  }
  return { analytics: categories.includes('performance'), marketing: categories.includes('targeting') };
};

/** Cookie Information: cookie "CookieInformationConsent" = JSON {consents_approved:['cookie_cat_marketing',..]} (unverified) */
export const readCookieInformation: CmpReader = cookies => {
  const consent = parseJson(cookies['CookieInformationConsent']);
  const approved = consent?.consents_approved;
  if (!Array.isArray(approved)) {
    return null;
  }
  return { analytics: approved.includes('cookie_cat_statistic'), marketing: approved.includes('cookie_cat_marketing') };
};

/** Own consent cookie written by ConsentService: analytics:yes,marketing:no */
export const readOwnConsent: CmpReader = cookies => {
  const values = parseKeyValueList(cookies[OWN_CONSENT_COOKIE]);
  if (!values || (values['analytics'] === undefined && values['marketing'] === undefined)) {
    return null;
  }
  return { analytics: values['analytics'] === CMP_YES, marketing: values['marketing'] === CMP_YES };
};

export function formatOwnConsent(consent: ICmpConsent): string {
  const toValue = (granted: boolean) => (granted ? CMP_YES : 'no');
  return `analytics:${toValue(consent.analytics)},marketing:${toValue(consent.marketing)}`;
}

/**
 * Consent management platforms whose consent cookie can be read from a subdomain.
 * Not readable: Usercentrics, Klaro and others that store consent in localStorage.
 */
export const CMP_READERS: { name: string; read: CmpReader }[] = [
  { name: 'Cookiebot', read: readCookiebot },
  { name: 'CookieYes', read: readCookieYes },
  { name: 'OneTrust', read: readOneTrust },
  { name: 'Complianz', read: readComplianz },
  { name: 'Cookie-Script', read: readCookieScript },
  { name: 'Cookie Information', read: readCookieInformation }
];
